import JSZip from 'jszip';
import { ProjectMedia, referenceImageDownloadName } from './projectReferenceLogic';
import { istNativeApp } from './nativePlatform';
import { dateiAusgeben } from './dateiAusgabe';

// Datei-Seite der Projektbilder: Download in Originalaufloesung, ZIP,
// verkleinerte Kopie fuer das PDF. Tabelle und Bucket-Upload liegen in
// projectMediaService.ts, die reine Logik in projectReferenceLogic.ts.

/** Bild als Blob laden (Bucket ist public, CORS offen). */
export const fetchReferenceImageBlob = async (image: ProjectMedia): Promise<Blob> => {
    const res = await fetch(image.url, { cache: 'no-store' });
    if (!res.ok) throw new Error(`Bild nicht erreichbar (HTTP ${res.status}).`);
    return res.blob();
};

const triggerDownload = (blob: Blob, fileName: string): Promise<void> => {
    // Android-App: Blob-Downloads kommen in der WebView nicht an → Datei in den
    // App-Cache und Teilen-Dialog. Fehler gehen an den Aufrufer (der zeigt sie an).
    if (istNativeApp()) return dateiAusgeben(blob, fileName);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
    return Promise.resolve();
};

/**
 * Einzeldownload in Originalaufloesung. Der Umweg ueber fetch/Blob ist
 * noetig: `<a download>` auf eine fremde Domain (Supabase) oeffnet das Bild
 * nur im Tab statt es zu speichern.
 */
export const downloadProjectReferenceImage = async (
    image: ProjectMedia,
    projectNumber?: string,
    projectName?: string,
    index?: number,
): Promise<void> => {
    const blob = await fetchReferenceImageBlob(image);
    await triggerDownload(blob, referenceImageDownloadName(projectNumber, projectName, image.mimeType || blob.type, index));
};

export interface ZipEntry {
    image: ProjectMedia;
    projectNumber?: string;
    projectName?: string;
    /** Laufende Nummer innerhalb des Projekts (1-basiert), wenn es mehrere Bilder gibt. */
    index?: number;
}

/**
 * Mehrere Bilder als ZIP. Laedt nacheinander (nicht parallel — 30 Bilder à
 * 20 MB gleichzeitig wuerden den Browser wuergen) und meldet den Fortschritt.
 * Bilder, die nicht erreichbar sind, werden uebersprungen und zurueckgemeldet.
 */
export const downloadProjectReferenceImagesZip = async (
    entries: ZipEntry[],
    onProgress?: (done: number, total: number) => void,
): Promise<{ ok: number; failed: string[] }> => {
    const zip = new JSZip();
    const failed: string[] = [];
    const usedNames = new Set<string>();
    let ok = 0;
    for (let i = 0; i < entries.length; i++) {
        const e = entries[i];
        try {
            const blob = await fetchReferenceImageBlob(e.image);
            let name = referenceImageDownloadName(e.projectNumber, e.projectName, e.image.mimeType || blob.type, e.index);
            if (usedNames.has(name)) name = name.replace(/(\.[a-z0-9]+)$/i, `_${i + 1}$1`);
            usedNames.add(name);
            zip.file(name, blob);
            ok++;
        } catch {
            failed.push(`${e.projectNumber || ''} ${e.projectName || ''}`.trim() || e.image.fileName);
        }
        onProgress?.(i + 1, entries.length);
    }
    if (ok > 0) {
        const out = await zip.generateAsync({ type: 'blob' });
        await triggerDownload(out, `Projektbilder_${new Date().toISOString().slice(0, 10)}.zip`);
    }
    return { ok, failed };
};

/** ZIP-Eintraege fuer eine Galerie: mit Index, sobald es mehr als ein Bild gibt. */
export const zipEntriesFor = (images: ProjectMedia[], projectNumber?: string, projectName?: string): ZipEntry[] =>
    images.map((image, i) => ({ image, projectNumber, projectName, index: images.length > 1 ? i + 1 : undefined }));

/**
 * Verkleinerte JPEG-Kopie als Data-URL fuer jsPDF. Das Original bleibt
 * unangetastet; ein 8000-px-Foto im PDF wuerde das Referenzblatt auf
 * zweistellige Megabyte aufblasen.
 */
export const loadReferenceImageForPdf = async (
    image: ProjectMedia,
    maxEdgePx = 1600,
): Promise<{ dataUrl: string; width: number; height: number } | null> => {
    try {
        const blob = await fetchReferenceImageBlob(image);
        const bmp = await createImageBitmap(blob);
        const scale = Math.min(1, maxEdgePx / Math.max(bmp.width, bmp.height));
        const w = Math.max(1, Math.round(bmp.width * scale));
        const h = Math.max(1, Math.round(bmp.height * scale));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) return null;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(bmp, 0, 0, w, h);
        bmp.close?.();
        return { dataUrl: canvas.toDataURL('image/jpeg', 0.85), width: w, height: h };
    } catch (e) {
        console.warn('[projektbilder] Bild fuer PDF nicht ladbar:', e);
        return null;
    }
};
